import { useAtomValue } from "jotai";
import {
	scannerRequestStateAtom,
	advancedSettingsAtom,
	examRequestErrorStateAtom,
} from "../../../../components/Atoms/atoms";
import { listOfAnswerOptions, listOfKeys } from "../../utils/common";
import CustomTooltip from "../../components/CustomTooltip";
import { Divider } from "@mui/material";

export default function NumberOfQuestionsInputs(props) {
	const { handleMCExamChange } = props;
	const state = useAtomValue(scannerRequestStateAtom);
	const advancedSettings = useAtomValue(advancedSettingsAtom);
	const errorState = useAtomValue(examRequestErrorStateAtom);
	return (
		<div className="pt-4 flex flex-col gap-4">
			<div className="flex flex-col border-gray-200 border rounded shadow p-4 gap-2">
				<label htmlFor="qLen" className="font-medium text-primary md:flex inline-block md:gap-1 ">
					Number of
					<CustomTooltip
						title="Answer Options"
						description="This setting tells our optical scanner how many answer bubbles to read for each question. This setting is NOT used to determine how many 'correct' options there are for each question."
						example="If your exam has some questions with 3 options (A,B,C) and others with 5 options (A,B,C,D,E), select 5."
					>
						<span className="underline inline md:px-0 px-1">answer options</span>
					</CustomTooltip>
					for each question:
					<span className="text-sm text-gray-500">(If varied, select the highest)</span>
				</label>
				<div className="hidden md:flex flex-col sm:flex-row gap-1 ">
					{listOfAnswerOptions.map((option) => (
						<button
							name="qLen"
							id="qLen"
							key={option.label}
							className={`flex items-center gap-2 px-2 py-1 border hover:border-primary rounded mb-1 ${
								state.qLen === option.value
									? "bg-primary text-white"
									: "bg-white border-gray-300 hover:bg-gray-50 text-gray-500"
							}`}
							onClick={() =>
								handleMCExamChange({
									target: { name: "qLen", value: option.value },
								})
							}
						>
							{option.label}
						</button>
					))}
				</div>
				<div className="md:hidden flex flex-col sm:flex-row gap-1 mb-2 mt-1">
					<select
						value={state.qLen}
						onChange={(e) =>
							handleMCExamChange({
								target: { name: "qLen", value: e.target.value },
							})
						}
						className="p-2 border border-gray-300 rounded"
					>
						{listOfAnswerOptions.map((option) => (
							<option key={option.label} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<Divider />
			<div className="flex flex-col border-gray-200 border rounded shadow p-4 gap-2">
				<label htmlFor="nKeys" className="font-medium text-primary">
					Number of versions/answer keys for your exam:
				</label>

				<div className="hidden md:flex flex-col sm:flex-row gap-2 mb-2 mt-1">
					{listOfKeys.map((option) => (
						<button
							disabled={
								(advancedSettings.formulaScoring && Number(option.value) > 4) ||
								(advancedSettings.compositeScoring && Number(option.value) > 5)
							}
							key={option.label}
							className={`flex items-center gap-2 px-2 py-1 border hover:border-primary rounded mb-1 disabled:bg-gray-300 disabled:cursor-not-allowed ${
								state.nKeys === option.value
									? "bg-primary text-white"
									: "bg-white border-gray-300 hover:bg-gray-50 text-gray-500"
							}`}
							onClick={() =>
								handleMCExamChange({
									target: { name: "nKeys", value: option.value },
								})
							}
						>
							{option.label}
						</button>
					))}
				</div>
				<div className="md:hidden flex flex-col sm:flex-row gap-2 mb-2 mt-1">
					<select
						value={state.nKeys}
						onChange={(e) =>
							handleMCExamChange({
								target: { name: "nKeys", value: e.target.value },
							})
						}
						className="p-2 border border-gray-300 rounded"
					>
						{listOfKeys.map((option) => (
							<option
								disabled={
									(advancedSettings.formulaScoring && Number(option.value) > 4) ||
									(advancedSettings.compositeScoring && Number(option.value) > 5)
								}
								key={option.label}
								value={option.value}
							>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<Divider />
			<div className="flex flex-col border-gray-200 border rounded shadow p-4 gap-2">
				<label htmlFor="hqNum" className="font-medium text-primary md:flex inline-block md:gap-1">
					<CustomTooltip
						title="Highest Question Number"
						placement="top"
						description="This setting tells our optical scanner which question number to read up to on the answer key(s) and student scantrons. If you have a regular contiguous exam, this would be equivalent to 'the number of questions on your exam', however some special case exams may have non-contiguous numbering."
						example="A 60 question exam administered with the standard blue scantrons that utilizes questions 1-50, and 201-210 as question 201 onward provide many more answer options. In this case, you would enter 210 as the highest question number, as configuring the scanner for the 'number of questions' (60) would result in the scanner reading only up to question 60 on any scantron sheets."
					>
						<span className="underline md:px-0 px-1">Highest question number</span>
					</CustomTooltip>
					on the exam:
				</label>
				<input
					autoFocus={!state.hqNum}
					type="number"
					id="hqNum"
					min="1"
					max="250"
					maxLength={3}
					name="hqNum"
					value={state.hqNum}
					onChange={handleMCExamChange}
					className={`mt-1 p-2 border rounded   ${
						errorState.hqNum ? "border-red-500" : "border-gray-300"
					} ${!state.hqNum ? "ring-2 ring-primaryGold animate-pulse" : ""}`}
				/>
			</div>
			<Divider />
		</div>
	);
}
